import { FormEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash-es";
import { BadgeCheck, Check, FileDown, ImageMinus, ImagePlus, Pencil, ThumbsUp, ThumbsDown, RefreshCcw, Zap } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { ChatMessage, Feedback } from "@/api/models";
import { getToolMessagesForAssistantTurn, parseCitationsFromToolMessage } from "@/utils/messageCitations";
import { addImageToPublicImages, getImageBlob, getImageSas, getSasUrl, removeImageFromPublicImages, reprocessFile, saveMessageFeedback } from "@/api/api";
import { getFileIcon } from '@/utils/fileValidation';
import { getSafeAzureBlobUrl } from "@/utils/safeUrls";
import Answer from "@/components/Answer/Answer";
import { linkifyText } from "@/components/InlineLink";
import ErrorDetails from "@/components/Message/ErrorDetails";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";
import ThinkingTrace from "@/components/Answer/ThinkingTrace";
import LoadingState from "@/components/Answer/LoadingState";
import UnhelpfulFeedbackContent from "@/components/Answer/UnhelpfulFeedbackContent";
import ReportInappropriateFeedbackContent from "@/components/Answer/ReportInappropriateFeedbackContent";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

enum messageStatus {
  NotRunning = "Not Running",
  Processing = "Processing",
  Done = "Done"
}

interface MessageProps {
  messages: ChatMessage[];
  message: ChatMessage;
  index: number;
  processMessages: messageStatus;
  resubmitUserMessage: () => void | Promise<void>;
  convDisabled: boolean;
  editUserMessage: string;
  setEditUserMessage: (id: string) => void;
  setEditUserMessageContent: (content: string) => void;
  onEnterPress: (ev: React.KeyboardEvent<Element>, answer: ChatMessage) => void;
  resubmitting: boolean;
  resubmitEditMessage: (answer: ChatMessage) => void;
  cancelEditMessage: () => void;
  isSelecting: boolean;
  isSummarizing: boolean;
  isQuerying: boolean;
  submitContinueMessage: () => void;
  isLoading: boolean;
  feedbackDisabled?: boolean;
  /** Files are still uploading/indexing — chips are shown but not yet openable. */
  filesPending?: boolean;
}

// Failures that repeat identically on resubmit, so Retry is not offered. Plain
// "content_filter" (output filtered mid-stream) is deliberately absent: resampling
// usually clears it.
const NO_RETRY_REASONS = new Set([
  "content_filter",
  "content_filter_prompt",
  "context_length_exceeded",
  "deployment_unavailable",
  "provider_auth",
  "invalid_prompt",
  "refusal",
]);

const Message = ({ messages, message, index, processMessages, resubmitUserMessage, convDisabled, editUserMessage, setEditUserMessage, setEditUserMessageContent, onEnterPress, resubmitting, resubmitEditMessage, cancelEditMessage, isSelecting, isSummarizing, isQuerying, submitContinueMessage, isLoading, feedbackDisabled: feedbackDisabledForMessage = false, filesPending = false } : MessageProps) => {
  const appStateContext = useContext(AppStateContext);
  const textareaRefEdit = useRef<HTMLTextAreaElement>(null);
  const [messagedCopied, setMessageCopied] = useState(false);
  const [addedToImagesLibrary, setAddedToImagesLibrary] = useState<boolean>(message.publicImage || false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([]);
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState<boolean>(false);
  const [otherFeedbackText, setOtherFeedbackText] = useState<string>('');
  const [blobImageUrl, setBlobImageUrl] = useState<string>('');
  const legacyBlobImageUrl = getSafeAzureBlobUrl(message.context);
  const [isReprocessing, setIsReprocessing] = useState<boolean>(false);
  const isOwnUserMessage = !message.senderName || message.senderName === appStateContext?.state.currentUser?.name;
  const isWeeklyQuotaError = message.error_code === "weekly_token_limit_reached" || message.error_type === "quota";
  const latestUserMessageIndex = messages.reduce(
    (latestIndex, candidate, candidateIndex) => candidate.role === "user" ? candidateIndex : latestIndex,
    -1,
  );
  const nextUserMessageIndex = messages.findIndex(
    (candidate, candidateIndex) => candidateIndex > index && candidate.role === "user",
  );
  const answerTurnEndIndex = nextUserMessageIndex === -1 ? messages.length : nextUserMessageIndex;
  const isFeedbackMessage = message.role === "assistant" || message.role === "image";
  const answerTurnHasError = isFeedbackMessage && messages
    .slice(index + 1, answerTurnEndIndex)
    .some((candidate) => candidate.role === "error");
  const feedbackDisabled = feedbackDisabledForMessage || (isFeedbackMessage
    && latestUserMessageIndex >= 0
    && ((isLoading && index > latestUserMessageIndex) || answerTurnHasError));
  const feedbackUnavailable = convDisabled || feedbackDisabled;
  const isStreamingPartialImage =
    message.role === "image" &&
    typeof message.content === "string" &&
    message.content.startsWith("data:image") &&
    index === messages.length - 1 &&
    processMessages === messageStatus.Processing;

  /**
   * Whether a directly-processed file has richer (table/image) content available
   * behind the manual "advanced processing" reprocess path. Keyed off the
   * transient upload-session state on individualFileChat.
   */
  const advancedAvailableFor = (filename: string): boolean =>
    !!appStateContext?.state.individualFileChat?.find((f) => f.filename === filename)?.advancedAvailable;

  /**
   * Force-OCR reprocess every file attached to this message in one request,
   * then mirror the returned message context into currentChat so the on-screen
   * conversation and next-turn payload match Cosmos.
   */
  const handleReExtractAll = async () => {
    const conversationId = appStateContext?.state.currentChat?.id;
    if (!conversationId || !message.files) return;
    // Only files that actually offer a richer extraction pass. A tabular file's
    // context is a derived map with no OCR tier behind it, and the button is
    // shown when *any* attached file qualifies -- so the list must be narrowed
    // here rather than assuming every file on the message is reprocessable.
    const filenames = message.files.split('|').filter(Boolean).filter(advancedAvailableFor);
    if (filenames.length === 0) return;

    setIsReprocessing(true);
    try {
      const resp = await reprocessFile(conversationId, message.id, filenames);
      if (resp && resp.status === 200) {
        const data = await resp.json();
        const chat = appStateContext?.state.currentChat;
        if (chat) {
          const updatedMessages = chat.messages.map((m) =>
            m.id === message.id ? { ...m, context: data.context } : m
          );
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: { ...chat, messages: updatedMessages } });
        }
      }
    } finally {
      setIsReprocessing(false);
    }
  };
  
  /**
   * Load SAS URL for blob reference images
  **/
  useEffect(() => {
    if (message.context && typeof message.context === 'string' && message.context.startsWith('###blob_ref###')) {
      const blobReference = message.context.substring(14);
      getImageSas(blobReference)
        .then(url => setBlobImageUrl(url))
        .catch(err => console.error('Failed to load image:', err));
    }
  }, [message.context]);
  
  /** 
   * Initializes the feedback value for a given answer
   *
   * @param {ChatMessage} message - The model's response object
  **/
  const initializeAnswerFeedback = (message: ChatMessage) => {
    if (message.id == undefined) return undefined;
    if (message.feedback == undefined) return undefined;
    if (message.feedback.split(",").length > 1) return Feedback.Negative;
    if (message.feedback.split("other-").length > 1) return Feedback.Negative;
    if (Object.values(Feedback).includes(message.feedback)) return message.feedback;
    return Feedback.Neutral;
  }
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(message));
  
  /** 
   * Handle setting feedback state on changes
  **/
  useEffect(() => {
    if (message.id == undefined) return;

    let currentFeedbackState;
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[message.id]) currentFeedbackState = appStateContext?.state.feedbackState[message.id];
    else currentFeedbackState = initializeAnswerFeedback(message);
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, message.id]);

  /** 
   * Handle checking if message is the latest summary message
  **/
  const isLatestSummary = () => {
    return index === messages.map((m, index) => ({m, index})).filter(({ m }) => m && m.role === 'summary').reduce((max, m) => Math.max(max, m.index), -1);
  };

  /* Answer keys its parse off this object's identity
     (`useMemo(() => parseAnswer(answer), [answer])`), so handing it a fresh
     literal made that memo a no-op: every re-render re-ran the citation
     JSON.parse, the markdown citation rewrite and a deep clone per citation --
     for every assistant message in the conversation, on every streaming chunk.
     Keyed on the values it is built from instead, so it only rebuilds when the
     answer or its citations actually change. */
  const citationToolMessages = getToolMessagesForAssistantTurn(messages, index);
  // getToolMessagesForAssistantTurn returns a freshly allocated array every
  // render, so using it directly as a useMemo dep below defeated the memo --
  // the identity always changed even when nothing in it did. Key on a
  // primitive built from the (small) tool-message subset instead. Tool content
  // is replaced wholesale rather than appended, so its length is enough to
  // detect a change -- interpolating the content itself would re-materialize
  // every citation payload on each of the renders this memo exists to cheapen.
  const citationToolMessagesKey = citationToolMessages
    .map((m) => `${m.id}:${typeof m.content === 'string' ? m.content.length : 0}`)
    .join('|');
  const answerPayload = useMemo(() => ({
    answer: message.content,
    citations: citationToolMessages.flatMap((candidate) => parseCitationsFromToolMessage(candidate)),
    message_id: message.id,
    feedback: message.feedback,
    model: message.model || ''
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on citationToolMessagesKey (primitive) rather than citationToolMessages, whose array identity the stream rebuilds every render
  }), [message.content, message.id, message.feedback, message.model, citationToolMessagesKey]);
  /**
   * Handle setting values to edit selected message
   *
   * @param {ChatMessage} message - The user's prompt
  **/
  const editMessage = (message: ChatMessage) => {
    setEditUserMessage(message.id);
    setEditUserMessageContent(message.content);
  }

  /** 
   * Handle copying only text without any styling
  **/
  const handleCopy = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const selection = window.getSelection();
    const selectedText = selection ? selection.toString() : '';
    if (event.clipboardData) event.clipboardData.setData('text/plain', selectedText);
  };

  /** 
   * Handle copying entire message content on button click
  **/
  const copyOnClick = () => {
    if (!navigator.clipboard) return;

    navigator.clipboard.writeText(message.content).then(() => {
      setMessageCopied(true);
      setTimeout(() => {
        setMessageCopied(false);
      }, 2000);
    });
  };

  /** 
   * Handle copying image on button click
  **/
  const copyImageOnClick = async () => {
    if (!navigator.clipboard) return;
    
    try {
      const clipboardItem = new ClipboardItem({
        'image/png': getImageBlob(message.content)
      });
      
      await navigator.clipboard.write([clipboardItem]);
      
      setMessageCopied(true);
      setTimeout(() => {
        setMessageCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy image:', err);
    }
  };

  /** 
   * Handle saving image on button click
  **/
  const saveImageOnClick = async () => {
    try {
      const blob = await getImageBlob(message.content);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${message.id || Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to save image:', err);
    }
  };

  /** 
   * Handle getting SAS URL for selected file and opening it
   *
   * @param {string} filename - The selected filename
  **/
  const onOpenFile = async (filename: string) => {
    var fileName = (appStateContext?.state.currentChat?.id || '') + '/' + (filename || '');
    var blob_name = 'fileconv';
    const file_sas_url = await getSasUrl(fileName, blob_name);
    let sas_url = file_sas_url.blob_url;
    if (sas_url) window.open(sas_url, "_blank");
  }
    
  /** 
   * Handle adding image to public images library
  **/
  const addToImagesLibrary = async () => {
    setAddedToImagesLibrary(true);
    const userMessage = appStateContext?.state.currentChat?.messages.slice().reverse().find(msg => msg.role === 'user' && msg.date < message.date);
    await addImageToPublicImages(appStateContext?.state.currentChat?.id || '', message.id || '', userMessage?.content || '', message.content || '');
  }
    
  /** 
   * Handle removing image from public images library
  **/
  const removeFromImagesLibrary = async () => {
    setAddedToImagesLibrary(false);
    await removeImageFromPublicImages(appStateContext?.state.currentChat?.id || '', message.id || '');
  }
  
  /** 
   * Handle "Thumbs Up" clicked
  **/
  const onLikeResponseClicked = () => {
    if (feedbackUnavailable || message.id == undefined) return;

    let newFeedbackState = feedbackState;
    if (feedbackState == Feedback.Positive) newFeedbackState = Feedback.Neutral;
    else newFeedbackState = Feedback.Positive;
    appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: message.id, feedback: newFeedbackState } });
    setFeedbackState(newFeedbackState);

    // Update message feedback in db
    saveMessageFeedback(message.id, newFeedbackState);
  }

  /** 
   * Handle "Thumbs Down" clicked
  **/
  const onDislikeResponseClicked = (e: React.MouseEvent) => {
    if (feedbackUnavailable || message.id == undefined) return;

    let newFeedbackState = feedbackState;
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative;
      setFeedbackState(newFeedbackState);
      appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: message.id, feedback: newFeedbackState } });
    }
    else {
      e.stopPropagation();
      setIsDialogOpen(false);
      newFeedbackState = Feedback.Neutral;
      setFeedbackState(newFeedbackState);
      appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: message.id, feedback: newFeedbackState } });
      saveMessageFeedback(message.id, Feedback.Neutral);
    }
  }

  /** 
   * Handle updating selected negative feedback options
  **/
  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>) => {
    if (feedbackUnavailable || message.id == undefined) return;
    let selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback;

    let feedbackList = negativeFeedbackList.slice();
    if ((ev?.target as HTMLInputElement).checked) feedbackList.push(selectedFeedback);
    else feedbackList = feedbackList.filter((f) => f !== selectedFeedback);

    setNegativeFeedbackList(feedbackList);
  }

  const updateReportInappropriateFeedback = (show: boolean) => {
    if (!feedbackUnavailable) setShowReportInappropriateFeedback(show);
  }

  const updateOtherFeedbackText = (text: string) => {
    if (!feedbackUnavailable) setOtherFeedbackText(text);
  }

  /** 
   * Handle submitted selected negative feedback options
  **/
  const onSubmitNegativeFeedback = async () => {
    if (feedbackUnavailable || message.id == undefined) return;
    
    // Build the feedback string
    const feedbackItems = negativeFeedbackList.map(feedback => {
      if (feedback === Feedback.OtherUnhelpful && otherFeedbackText.trim()) {
        return `other-${otherFeedbackText.trim()}`;
      }
      return feedback;
    });
    
    saveMessageFeedback(message.id, feedbackItems.join(","));
    setShowReportInappropriateFeedback(false);
    setNegativeFeedbackList([]);
    setOtherFeedbackText('');
  }

  /**
   * Helper function to format timestamp into YYYY-mm-dd HH:ii
   *
   * @param dateStr - The date string to format.
  **/
  const formatDateTime = (dateStr: string) => {
    let date = new Date(dateStr);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  return (
    <>
      {message.role === "user" ? (
        <>
          <div className="my-1" data-user-message-id={message.id}>
            {message.senderName && (
              <div className={`flex ${isOwnUserMessage ? `justify-end` : `justify-start`}`}>
                <div className={`flex flex-col gap-1 w-full ${isOwnUserMessage ? `items-end` : `items-start mt-4`} mb-1`}>
                  <span className="text-sm dark:text-white/75 px-1">{message.senderName}</span>
                </div>
              </div>
            )}
            {(message.files && !isEmpty(message.files) && (message.files.split('|').length || 0) > 0) ?
              <div className="group my-1">
                <div className={`flex ${isOwnUserMessage ? `justify-end` : `justify-start`}`}>
                  <div className="max-w-[95%] user-prompt dark:text-white">
                    <div className="flex flex-wrap items-end gap-2">
                      {message.files.split('|').map((filename, idx) =>
                        <div key={idx} className={`flex items-center rounded-xl gap-1 ${filesPending ? `opacity-60` : `hover:bg-base-100/75 ${(location.pathname.substring(0, 7) != '/share/' ? `cursor-pointer` : ``)}`}`}>
                          <div className="flex items-center text-xs py-1.5 px-2 gap-1" onClick={() => { if (!filesPending && location.pathname.substring(0, 7) != '/share/') onOpenFile(filename) }}>
                            <img src={getFileIcon(filename)} width="16" />
                            {filename}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            : null}
            <div className="group my-1">
              <div className={`flex ${isOwnUserMessage ? `justify-end` : `justify-start`}`}>
                <div className={`${editUserMessage === message.id ? `w-full` : `max-w-[95%]`} bg-base-100 rounded-3xl text-sm dark:text-white user-prompt py-3 px-4`} onCopy={handleCopy}>
                  {editUserMessage === message.id ? (
                    <div tabIndex={0}>
                      <textarea
                        ref={textareaRefEdit}
                        autoComplete="off"
                        className="w-full bg-transparent border border-secondary rounded p-2"
                        defaultValue={message.content}
                        onChange={(e) => setEditUserMessageContent(e.target.value)}
                        onKeyDown={(e) => onEnterPress(e, message)}
                        disabled={resubmitting}
                        autoFocus={true}
                        maxLength={10000}
                      ></textarea>
                      {!resubmitting ? (
                        <div className="flex flex-row justify-end gap-3">
                          <button type="button" className="btn btn-sm btn-secondary" onClick={cancelEditMessage}>Cancel</button>
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => resubmitEditMessage(message)}>Send</button>
                        </div>
                      ) : (
                        <div><span className="loading loading-spinner loading-sm"></span></div>
                      )}
                    </div>
                  ) : (
                    <div>
                      {/* New format: Blob reference - load SAS URL dynamically */}
                      {message.context && typeof message.context === 'string' && message.context.startsWith('###blob_ref###') && blobImageUrl && <img className="scaled-image rounded-xl mb-2" src={blobImageUrl} alt="Uploaded image" />}
                      {/* Legacy format: Base64 encoded image (for backwards compatibility) */}
                      {message.context && typeof message.context === 'string' && message.context.startsWith('###image###') && <img className="scaled-image rounded-xl mb-2" src={`data:image/png;base64,${message.context.substring(11)}`} alt="Uploaded image" />}
                      {/* Legacy remote image URL format */}
                      {legacyBlobImageUrl && <img className="scaled-image rounded-xl mb-2" src={legacyBlobImageUrl} alt="Uploaded image" />}
                      
                      <pre className="whitespace-pre-wrap break-words">{linkifyText(message.content)}</pre>

                      <div className="flex items-end justify-between gap-2">
                        <div className="flex"></div>
                        <div className="flex text-[11px] text-end text-black/25 dark:text-secondary min-w-fit mt-1">{formatDateTime(message.date)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={`flex flex-row items-center gap-3 m-2 mb-0 opacity-0 group-hover:opacity-100 transition-opacity ${isOwnUserMessage ? `justify-end` : `justify-start`}`}>
                {!isEmpty(message.files) && !filesPending ? 
                  <div className="flex flex-row items-center gap-1 text-[12px] text-accent">
                    <BadgeCheck size={12} /> {message.direct_files ? `Processed Directly` : `Files Indexed`}
                  </div>
                : null}
                {message.direct_files && message.files && message.files.split('|').some(advancedAvailableFor) && location.pathname.substring(0, 7) != '/share/' && (
                  <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Re-run files with advanced processing">
                    {isReprocessing ? 
                      <span className="loading loading-spinner loading-xs"></span>
                    : 
                      <Zap className="cursor-pointer" size={18} onClick={handleReExtractAll} />
                    }
                  </div>
                )}
                {messagedCopied ?
                  <Check className="text-success" size={18} />
                :
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon cursor-pointer" onClick={copyOnClick}><path d="M12.668 10.667C12.668 9.95614 12.668 9.46258 12.6367 9.0791C12.6137 8.79732 12.5758 8.60761 12.5244 8.46387L12.4688 8.33399C12.3148 8.03193 12.0803 7.77885 11.793 7.60254L11.666 7.53125C11.508 7.45087 11.2963 7.39395 10.9209 7.36328C10.5374 7.33197 10.0439 7.33203 9.33301 7.33203H6.5C5.78896 7.33203 5.29563 7.33195 4.91211 7.36328C4.63016 7.38632 4.44065 7.42413 4.29688 7.47559L4.16699 7.53125C3.86488 7.68518 3.61186 7.9196 3.43555 8.20703L3.36524 8.33399C3.28478 8.49198 3.22795 8.70352 3.19727 9.0791C3.16595 9.46259 3.16504 9.95611 3.16504 10.667V13.5C3.16504 14.211 3.16593 14.7044 3.19727 15.0879C3.22797 15.4636 3.28473 15.675 3.36524 15.833L3.43555 15.959C3.61186 16.2466 3.86474 16.4807 4.16699 16.6348L4.29688 16.6914C4.44063 16.7428 4.63025 16.7797 4.91211 16.8027C5.29563 16.8341 5.78896 16.835 6.5 16.835H9.33301C10.0439 16.835 10.5374 16.8341 10.9209 16.8027C11.2965 16.772 11.508 16.7152 11.666 16.6348L11.793 16.5645C12.0804 16.3881 12.3148 16.1351 12.4688 15.833L12.5244 15.7031C12.5759 15.5594 12.6137 15.3698 12.6367 15.0879C12.6681 14.7044 12.668 14.211 12.668 13.5V10.667ZM13.998 12.665C14.4528 12.6634 14.8011 12.6602 15.0879 12.6367C15.4635 12.606 15.675 12.5492 15.833 12.4688L15.959 12.3975C16.2466 12.2211 16.4808 11.9682 16.6348 11.666L16.6914 11.5361C16.7428 11.3924 16.7797 11.2026 16.8027 10.9209C16.8341 10.5374 16.835 10.0439 16.835 9.33301V6.5C16.835 5.78896 16.8341 5.29563 16.8027 4.91211C16.7797 4.63025 16.7428 4.44063 16.6914 4.29688L16.6348 4.16699C16.4807 3.86474 16.2466 3.61186 15.959 3.43555L15.833 3.36524C15.675 3.28473 15.4636 3.22797 15.0879 3.19727C14.7044 3.16593 14.211 3.16504 13.5 3.16504H10.667C9.9561 3.16504 9.46259 3.16595 9.0791 3.19727C8.79739 3.22028 8.6076 3.2572 8.46387 3.30859L8.33399 3.36524C8.03176 3.51923 7.77886 3.75343 7.60254 4.04102L7.53125 4.16699C7.4508 4.32498 7.39397 4.53655 7.36328 4.91211C7.33985 5.19893 7.33562 5.54719 7.33399 6.00195H9.33301C10.022 6.00195 10.5791 6.00131 11.0293 6.03809C11.4873 6.07551 11.8937 6.15471 12.2705 6.34668L12.4883 6.46875C12.984 6.7728 13.3878 7.20854 13.6533 7.72949L13.7197 7.87207C13.8642 8.20859 13.9292 8.56974 13.9619 8.9707C13.9987 9.42092 13.998 9.97799 13.998 10.667V12.665ZM18.165 9.33301C18.165 10.022 18.1657 10.5791 18.1289 11.0293C18.0961 11.4302 18.0311 11.7914 17.8867 12.1279L17.8203 12.2705C17.5549 12.7914 17.1509 13.2272 16.6553 13.5313L16.4365 13.6533C16.0599 13.8452 15.6541 13.9245 15.1963 13.9619C14.8593 13.9895 14.4624 13.9935 13.9951 13.9951C13.9935 14.4624 13.9895 14.8593 13.9619 15.1963C13.9292 15.597 13.864 15.9576 13.7197 16.2939L13.6533 16.4365C13.3878 16.9576 12.9841 17.3941 12.4883 17.6982L12.2705 17.8203C11.8937 18.0123 11.4873 18.0915 11.0293 18.1289C10.5791 18.1657 10.022 18.165 9.33301 18.165H6.5C5.81091 18.165 5.25395 18.1657 4.80371 18.1289C4.40306 18.0962 4.04235 18.031 3.70606 17.8867L3.56348 17.8203C3.04244 17.5548 2.60585 17.151 2.30176 16.6553L2.17969 16.4365C1.98788 16.0599 1.90851 15.6541 1.87109 15.1963C1.83431 14.746 1.83496 14.1891 1.83496 13.5V10.667C1.83496 9.978 1.83432 9.42091 1.87109 8.9707C1.90851 8.5127 1.98772 8.10625 2.17969 7.72949L2.30176 7.51172C2.60586 7.0159 3.04236 6.6122 3.56348 6.34668L3.70606 6.28027C4.04237 6.136 4.40303 6.07083 4.80371 6.03809C5.14051 6.01057 5.53708 6.00551 6.00391 6.00391C6.00551 5.53708 6.01057 5.14051 6.03809 4.80371C6.0755 4.34588 6.15483 3.94012 6.34668 3.56348L6.46875 3.34473C6.77282 2.84912 7.20856 2.44514 7.72949 2.17969L7.87207 2.11328C8.20855 1.96886 8.56979 1.90385 8.9707 1.87109C9.42091 1.83432 9.978 1.83496 10.667 1.83496H13.5C14.1891 1.83496 14.746 1.83431 15.1963 1.87109C15.6541 1.90851 16.0599 1.98788 16.4365 2.17969L16.6553 2.30176C17.151 2.60585 17.5548 3.04244 17.8203 3.56348L17.8867 3.70606C18.031 4.04235 18.0962 4.40306 18.1289 4.80371C18.1657 5.25395 18.165 5.81091 18.165 6.5V9.33301Z"></path></svg>
                }
                {!convDisabled && <Pencil className="cursor-pointer" size={16} onClick={() => editMessage(message)} />}
              </div>
            </div>
          </div>

          {(index === messages.length - 1 && isQuerying) && (
            <div className="pt-3 px-1">
              <LoadingState label="Querying Data" />
            </div>
          )}
        </>
      )
      : (message.role === "selection") ? (
        <div className="pt-3 px-1">
          <ThinkingTrace
            working={false}
            activeLabel="Choosing an assistant"
            settledLabel={!isEmpty(message.content) ? `Selected ${message.content}` : 'Using General Knowledge'}
          />
        </div>
      )
      : (message.role === "summary") ? (
        <div className="px-1">
          <ThinkingTrace
            reasoning={message.content}
            working={isLatestSummary() && isSummarizing}
            activeLabel="Thinking"
            settledLabel="Thought for {s}s"
          />
        </div>
      )
      : (message.role === "warning") ? (
        <div className="flex truncation-warning mt-2 mb-6">
          <div className="max-w-[95%] border border-warning bg-warning bg-opacity-20 rounded-3xl text-sm dark:text-white py-3 px-4">
            <div>{message.content}</div>
            <div className="text-center"><button type="button" className="btn btn-sm btn-warning mt-2" onClick={submitContinueMessage}>Continue</button></div>
          </div>
        </div>
      )
      : (message.role === "assistant") ? (
        <>
          <Answer
            answer={answerPayload}
            status={(index === messages.length - 1 ? processMessages : 'Done')}
            resubmitUserMessage={resubmitUserMessage}
            latest={index === messages.length - 1 ? true : false}
            convDisabled={convDisabled}
            feedbackDisabled={feedbackDisabled}
            terminateFileUpload={() => {}}
          />
        </>
      )
      : (message.role === "image") ? (
        <div className="mt-2 mb-6">
          <div className="flex">
            <div className={`${isStreamingPartialImage ? 'animate-pulse brightness-50' : ''}`}>
              <div className="relative">
                <img className={`${!isStreamingPartialImage ? 'cursor-pointer' : ''} scaled-image rounded-3xl`} src={message.content} alt="Image Expired" onClick={() => { if (!isStreamingPartialImage) window.open(message.content, `_blank`) }} />
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center gap-4 m-2 mb-0">
            {messagedCopied ?
              <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Copied">
                <Check className="text-success" size={18} />
              </div>
            :
              <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Copy">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon cursor-pointer" onClick={copyImageOnClick}><path d="M12.668 10.667C12.668 9.95614 12.668 9.46258 12.6367 9.0791C12.6137 8.79732 12.5758 8.60761 12.5244 8.46387L12.4688 8.33399C12.3148 8.03193 12.0803 7.77885 11.793 7.60254L11.666 7.53125C11.508 7.45087 11.2963 7.39395 10.9209 7.36328C10.5374 7.33197 10.0439 7.33203 9.33301 7.33203H6.5C5.78896 7.33203 5.29563 7.33195 4.91211 7.36328C4.63016 7.38632 4.44065 7.42413 4.29688 7.47559L4.16699 7.53125C3.86488 7.68518 3.61186 7.9196 3.43555 8.20703L3.36524 8.33399C3.28478 8.49198 3.22795 8.70352 3.19727 9.0791C3.16595 9.46259 3.16504 9.95611 3.16504 10.667V13.5C3.16504 14.211 3.16593 14.7044 3.19727 15.0879C3.22797 15.4636 3.28473 15.675 3.36524 15.833L3.43555 15.959C3.61186 16.2466 3.86474 16.4807 4.16699 16.6348L4.29688 16.6914C4.44063 16.7428 4.63025 16.7797 4.91211 16.8027C5.29563 16.8341 5.78896 16.835 6.5 16.835H9.33301C10.0439 16.835 10.5374 16.8341 10.9209 16.8027C11.2965 16.772 11.508 16.7152 11.666 16.6348L11.793 16.5645C12.0804 16.3881 12.3148 16.1351 12.4688 15.833L12.5244 15.7031C12.5759 15.5594 12.6137 15.3698 12.6367 15.0879C12.6681 14.7044 12.668 14.211 12.668 13.5V10.667ZM13.998 12.665C14.4528 12.6634 14.8011 12.6602 15.0879 12.6367C15.4635 12.606 15.675 12.5492 15.833 12.4688L15.959 12.3975C16.2466 12.2211 16.4808 11.9682 16.6348 11.666L16.6914 11.5361C16.7428 11.3924 16.7797 11.2026 16.8027 10.9209C16.8341 10.5374 16.835 10.0439 16.835 9.33301V6.5C16.835 5.78896 16.8341 5.29563 16.8027 4.91211C16.7797 4.63025 16.7428 4.44063 16.6914 4.29688L16.6348 4.16699C16.4807 3.86474 16.2466 3.61186 15.959 3.43555L15.833 3.36524C15.675 3.28473 15.4636 3.22797 15.0879 3.19727C14.7044 3.16593 14.211 3.16504 13.5 3.16504H10.667C9.9561 3.16504 9.46259 3.16595 9.0791 3.19727C8.79739 3.22028 8.6076 3.2572 8.46387 3.30859L8.33399 3.36524C8.03176 3.51923 7.77886 3.75343 7.60254 4.04102L7.53125 4.16699C7.4508 4.32498 7.39397 4.53655 7.36328 4.91211C7.33985 5.19893 7.33562 5.54719 7.33399 6.00195H9.33301C10.022 6.00195 10.5791 6.00131 11.0293 6.03809C11.4873 6.07551 11.8937 6.15471 12.2705 6.34668L12.4883 6.46875C12.984 6.7728 13.3878 7.20854 13.6533 7.72949L13.7197 7.87207C13.8642 8.20859 13.9292 8.56974 13.9619 8.9707C13.9987 9.42092 13.998 9.97799 13.998 10.667V12.665ZM18.165 9.33301C18.165 10.022 18.1657 10.5791 18.1289 11.0293C18.0961 11.4302 18.0311 11.7914 17.8867 12.1279L17.8203 12.2705C17.5549 12.7914 17.1509 13.2272 16.6553 13.5313L16.4365 13.6533C16.0599 13.8452 15.6541 13.9245 15.1963 13.9619C14.8593 13.9895 14.4624 13.9935 13.9951 13.9951C13.9935 14.4624 13.9895 14.8593 13.9619 15.1963C13.9292 15.597 13.864 15.9576 13.7197 16.2939L13.6533 16.4365C13.3878 16.9576 12.9841 17.3941 12.4883 17.6982L12.2705 17.8203C11.8937 18.0123 11.4873 18.0915 11.0293 18.1289C10.5791 18.1657 10.022 18.165 9.33301 18.165H6.5C5.81091 18.165 5.25395 18.1657 4.80371 18.1289C4.40306 18.0962 4.04235 18.031 3.70606 17.8867L3.56348 17.8203C3.04244 17.5548 2.60585 17.151 2.30176 16.6553L2.17969 16.4365C1.98788 16.0599 1.90851 15.6541 1.87109 15.1963C1.83431 14.746 1.83496 14.1891 1.83496 13.5V10.667C1.83496 9.978 1.83432 9.42091 1.87109 8.9707C1.90851 8.5127 1.98772 8.10625 2.17969 7.72949L2.30176 7.51172C2.60586 7.0159 3.04236 6.6122 3.56348 6.34668L3.70606 6.28027C4.04237 6.136 4.40303 6.07083 4.80371 6.03809C5.14051 6.01057 5.53708 6.00551 6.00391 6.00391C6.00551 5.53708 6.01057 5.14051 6.03809 4.80371C6.0755 4.34588 6.15483 3.94012 6.34668 3.56348L6.46875 3.34473C6.77282 2.84912 7.20856 2.44514 7.72949 2.17969L7.87207 2.11328C8.20855 1.96886 8.56979 1.90385 8.9707 1.87109C9.42091 1.83432 9.978 1.83496 10.667 1.83496H13.5C14.1891 1.83496 14.746 1.83431 15.1963 1.87109C15.6541 1.90851 16.0599 1.98788 16.4365 2.17969L16.6553 2.30176C17.151 2.60585 17.5548 3.04244 17.8203 3.56348L17.8867 3.70606C18.031 4.04235 18.0962 4.40306 18.1289 4.80371C18.1657 5.25395 18.165 5.81091 18.165 6.5V9.33301Z"></path></svg>
              </div>
            }
            <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Export">
              <FileDown size={18} className="icon cursor-pointer" onClick={saveImageOnClick} />
            </div>
            {!convDisabled && (<>
              {!addedToImagesLibrary ? 
                <button type="button" className="cursor-pointer tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Add Image & Conversation to Public Images Library" onClick={addToImagesLibrary}>
                  <ImagePlus size={18} className="hover:text-success" />
                </button>
              :
                <button type="button" className="cursor-pointer tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Remove from Public Images Library" onClick={removeFromImagesLibrary}>
                  <ImageMinus size={18} className="hover:text-error" />
                </button>
              }
            </>)}
            <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Good response">
              <button type="button" aria-label="Good response" className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50" disabled={feedbackUnavailable} onClick={onLikeResponseClicked}>
                <ThumbsUp aria-hidden="true" className={(feedbackState === Feedback.Positive || appStateContext?.state.feedbackState[message.id] === Feedback.Positive) ? `text-success` : ``} size={16} />
              </button>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open || !feedbackUnavailable) setIsDialogOpen(open); }}>
              <DialogTrigger className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Bad response" aria-label="Bad response" disabled={feedbackUnavailable}>
                <ThumbsDown aria-hidden="true" className={`${!feedbackUnavailable ? `cursor-pointer` : ``} ${(feedbackState !== Feedback.Positive && feedbackState !== Feedback.Neutral && feedbackState !== undefined) ? `text-error` : ``}`} size={16} onClick={onDislikeResponseClicked} />
              </DialogTrigger>
              <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short" aria-describedby={undefined}>
                <DialogHeader className="text-sm">
                  <DialogTitle className="mb-3">Submit Feedback</DialogTitle>

                  <div>Your feedback will improve this experience.</div>

                  {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent negativeFeedbackList={negativeFeedbackList} updateFeedbackList={updateFeedbackList} setShowReportInappropriateFeedback={updateReportInappropriateFeedback} otherFeedbackText={otherFeedbackText} setOtherFeedbackText={updateOtherFeedbackText} /> : <ReportInappropriateFeedbackContent negativeFeedbackList={negativeFeedbackList} updateFeedbackList={updateFeedbackList} />}

                  <div>By pressing submit, your feedback will be visible to the application owner.</div>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
                  <DialogClose asChild><button type="button" className="btn btn-primary" disabled={negativeFeedbackList.length < 1 || feedbackUnavailable} onClick={onSubmitNegativeFeedback}>Submit</button></DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {!convDisabled && (index === messages.length - 1) && (index === messages.length - 1 ? processMessages : 'Done') !== 'Processing' ? <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Try again"><RefreshCcw className="cursor-pointer" size={16} onClick={resubmitUserMessage} /></div> : null}
          </div>
        </div>
      )
      : (message.role === "error") ? (
        (() => {
          const details = getErrorDetails(message.content, message.error_details);
          const errorSummary = getErrorSummary(message.content, details);
          const safetyBlocked = [message.content, details.upstream_detail].some((value) =>
            typeof value === "string" && value.includes("Request was rejected as a result of the safety system."),
          );
          const canRetry = !convDisabled
            && !isWeeklyQuotaError
            && !NO_RETRY_REASONS.has(message.error_details?.reason || "")
            && !safetyBlocked;
          return (
        <div className="flex mt-2 mb-6">
          <div className={`max-w-[95%] rounded-3xl text-sm dark:text-white model-response py-3 px-4 ${isWeeklyQuotaError ? 'border border-warning bg-warning/15' : 'border border-error bg-error bg-opacity-50'}`} onCopy={handleCopy}>
            <div>
              {isWeeklyQuotaError ? (
                <div className="mt-1 ms-1">
                  <div className="font-semibold text-warning-content dark:text-warning">Weekly token limit reached</div>
                  <div className="mt-2">{message.content}</div>
                </div>
              ) : (
                <div className="mt-2 ms-2">
                  {errorSummary}
                </div>
              )}
              <ErrorDetails
                details={details}
                error={message.content}
                onRetry={canRetry ? resubmitUserMessage : undefined}
                retryBusy={resubmitting || isLoading || processMessages === messageStatus.Processing}
              />
            </div>
          </div>
        </div>
          );
        })()
      )
      : null}
    </>
  );
}

export default Message;
